import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  ArrowRight,
  AvatarLabelGroup,
  Badge,
  Button,
  Card,
  CardHeader,
  ChevronRight,
  CopyButton,
  Metric,
  Table,
  TextInput,
} from "@opendate-app/setlist";
import useTabData from "../../EventPage/hooks/useTabData";
import { formatPrice } from "../../../shared/teamCurrencyFormatting";
import { DetailColumns } from "../../ClassesShared/DetailPageLayout";
import ClassFormatBadge from "../../ClassesShared/ClassFormatBadge";
import ClassCapacityMeter from "../../ClassesShared/ClassCapacityMeter";
import initialsFor from "../../ClassesShared/initials";
import personColorProps from "../../ClassesShared/personColor";
import { VISIBILITY_LABELS, calendarDaysBetween, formatRegisteredAt, formatSessionRange, heldForWaitlistLabel } from "../../ClassesShared/classLabels";
import { registrationStatusBadge } from "../constants/registrationStatus";
import DetailField from "../registrationPage/DetailField";
import "../../ClassesShared/classesShared.css";
import ClassesLoading from "../../ClassesShared/ClassesLoading";

export function startCountdown(firstSessionAt, started, timeZone, now = Date.now()) {
  if (!firstSessionAt) return null;
  const days = Math.abs(calendarDaysBetween(now, firstSessionAt, timeZone));
  if (days === 0) return { started, label: "Today" };
  return { started, label: started ? `${days}d ago` : `${days}d` };
}

export function instructorSessionsLabel(instructorId, sessions) {
  const taught = sessions.filter((session) => (session.instructors || []).some((instructor) => instructor.id === instructorId));
  if (sessions.length > 1 && taught.length === sessions.length) return "All sessions";
  return taught.map((session) => session.name).join(", ");
}

function uniqueInstructors(sessions) {
  const byId = new Map();
  sessions.forEach((session) => (session.instructors || []).forEach((instructor) => byId.set(instructor.id, instructor)));
  return [...byId.values()];
}

export function waitlistNote(classInstance, waitlistCount) {
  if (classInstance.terminal_status) return "Waitlist closed";
  if (!classInstance.waitlist_enabled) return "Waitlist is off";
  if (!classInstance.waitlist_open || classInstance.registration_closed_reason) return "Not taking new sign-ups";
  switch (classInstance.public_state) {
    case "waitlist":
      return waitlistCount > 0 ? "Class is full · next in line gets the first open spot" : "Class is full · new sign-ups join the waitlist";
    case "sold_out":
    case "registration_closed":
      return "Not taking new sign-ups";
    case "opens_soon":
      return "Activates at full capacity once registration opens";
    default:
      return "Activates at full capacity";
  }
}

export function publicPageNote(status, visibility, publiclyViewable, registrationClosed = false) {
  if (publiclyViewable && registrationClosed) return "Registration has closed, so the page no longer takes sign-ups.";
  if (publiclyViewable) return "Share this URL or embed it on your site to drive registrations.";
  if (status === "canceled") return "This class was canceled, so people can no longer register from its page.";
  if (status === "completed") return "This class has ended, so people can no longer register from its page.";
  if (status === "published" && visibility === "private") return "This class is private, so only your team can open its page.";
  return "Only your team can open this page until the class is published.";
}

function Metrics({ stats, classInstance, currencyCode, timeZone }) {
  const canceled = classInstance.status === "canceled";
  const countdown = canceled ? null : startCountdown(stats.firstSessionAt, stats.started, timeZone);
  const firstSession = stats.firstSessionAt
    ? new Date(stats.firstSessionAt).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: timeZone || undefined })
    : null;
  const note = (text) => <span className="small classes-text-secondary">{text}</span>;

  return (
    <div className="row">
      <div className="col-sm-6 col-xl-3 mb-4">
        <Metric
          className="h-100"
          label="Registered"
          value={`${canceled ? "—" : classInstance.registered_count ?? 0} / ${classInstance.capacity ?? "—"}`}
          change={stats.registrationsThisWeek > 0 ? { value: `+${stats.registrationsThisWeek}`, direction: "up", label: "this week" } : undefined}
          chart={(
            <>
              <ClassCapacityMeter taken={classInstance.spots_taken || 0} capacity={classInstance.capacity} percent={classInstance.capacity_percent} level={classInstance.capacity_level} status={classInstance.status} />
              {classInstance.held_count > 0 && note(heldForWaitlistLabel(classInstance.held_count))}
            </>
          )}
        />
      </div>
      <div className="col-sm-6 col-xl-3 mb-4">
        <Metric
          className="h-100"
          label="Waitlist"
          value={stats.waitlist ?? 0}
          chart={note(waitlistNote(classInstance, stats.waitlist ?? 0))}
        />
      </div>
      <div className="col-sm-6 col-xl-3 mb-4">
        <Metric
          className="h-100"
          label="Net revenue"
          value={stats.netRevenue != null ? formatPrice(Number(stats.netRevenue), currencyCode) : "—"}
          chart={note("Your share of paid registrations, as in Class Sales")}
        />
      </div>
      <div className="col-sm-6 col-xl-3 mb-4">
        <Metric
          className="h-100"
          label={countdown?.started ? "Started" : "Days to start"}
          value={countdown ? countdown.label : "—"}
          chart={note(canceled ? "Class canceled" : firstSession ? `First session ${firstSession}` : "No sessions yet")}
        />
      </div>
    </div>
  );
}

Metrics.propTypes = { stats: PropTypes.object.isRequired, classInstance: PropTypes.object.isRequired, currencyCode: PropTypes.string, timeZone: PropTypes.string };

function PublicPageCard({ publicUrl, publiclyViewable, status, visibility, registrationClosed }) {
  return (
    <Card
      header={(
        <CardHeader
          title="Public Class Page"
          supportingText={publicPageNote(status, visibility, publiclyViewable, registrationClosed)}
        />
      )}
    >
      <div className="d-flex align-items-center classes-gap-12">
        <div className="flex-grow-1">
          <TextInput aria-label="Public class page URL" value={publicUrl} readOnly onChange={() => {}} />
        </div>
        <CopyButton value={publicUrl} />
      </div>
    </Card>
  );
}

PublicPageCard.propTypes = { publicUrl: PropTypes.string.isRequired, publiclyViewable: PropTypes.bool, status: PropTypes.string, visibility: PropTypes.string, registrationClosed: PropTypes.bool };

function RecentRegistrationsCard({ registrations, registrationsPath, timeZone, onViewRegistrations }) {
  const columns = [
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (registration) => (
        <AvatarLabelGroup size="sm" initials={initialsFor(registration.full_name)} {...personColorProps(registration.full_name)} name={registration.full_name} supportingText={registration.email} />
      ),
    },
    { key: "registered", header: "Registered", render: (registration) => formatRegisteredAt(registration.created_at, timeZone) },
    {
      key: "status",
      header: "Status",
      render: (registration) => {
        const badge = registrationStatusBadge(registration);
        return <Badge color={badge.color} size="sm" dot>{badge.label}</Badge>;
      },
    },
    {
      key: "open",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      render: (registration) => (
        <Button as="a" variant="tertiary" size="sm" icon={ChevronRight} href={`${registrationsPath}/${registration.id}`} aria-label={`Open ${registration.full_name}`} />
      ),
    },
  ];

  return (
    <Card
      padding="none"
      header={(
        <CardHeader
          title="Recent Registrations"
          supportingText={registrations.length > 0 ? `The ${registrations.length} most recent` : null}
          actions={registrations.length > 0 && <Button variant="link-color" trailingIcon={ArrowRight} onClick={onViewRegistrations}>View All</Button>}
        />
      )}
    >
      <Table className="classes-table" columns={columns} data={registrations} getRowKey={(registration) => registration.id} stackBelow={560} empty="No registrations yet." />
    </Card>
  );
}

RecentRegistrationsCard.propTypes = {
  registrations: PropTypes.array.isRequired,
  registrationsPath: PropTypes.string.isRequired,
  timeZone: PropTypes.string,
  onViewRegistrations: PropTypes.func.isRequired,
};

function ClassDetailsCard({ classInstance, currencyCode, timeZone }) {
  const sessions = classInstance.class_sessions || [];
  return (
    <Card header={<CardHeader title="Class Details" />}>
      <dl className="row mb-0">
        <DetailField label="Template" wide>{classInstance.template_name}</DetailField>
        <DetailField label="Format" wide><ClassFormatBadge format={classInstance.format} /></DetailField>
        <DetailField label="Visibility" wide>{VISIBILITY_LABELS[classInstance.visibility]}</DetailField>
        <DetailField label="Price" wide>{classInstance.price != null ? `${formatPrice(Number(classInstance.price), currencyCode)} per spot` : null}</DetailField>
        <DetailField label="Sessions" wide>
          {sessions.length > 0 && (
            <ul className="list-unstyled mb-0">
              {sessions.map((session) => (
                <li key={session.id}>
                  <span className="font-weight-bold">{session.name}</span>
                  <span className="classes-text-secondary">{` — ${formatSessionRange(session.starts_at, session.ends_at, timeZone)}`}</span>
                </li>
              ))}
            </ul>
          )}
        </DetailField>
      </dl>
    </Card>
  );
}

ClassDetailsCard.propTypes = { classInstance: PropTypes.object.isRequired, currencyCode: PropTypes.string, timeZone: PropTypes.string };

function InstructorsCard({ sessions }) {
  const instructors = uniqueInstructors(sessions);
  return (
    <Card header={<CardHeader title="Instructors" />}>
      {instructors.length === 0 && <div className="classes-text-secondary">No instructors assigned yet.</div>}
      <div className="d-flex flex-column classes-gap-16">
        {instructors.map((instructor) => (
          <AvatarLabelGroup
            key={instructor.id}
            size="lg"
            src={instructor.photo_url || undefined}
            initials={initialsFor(instructor.name)}
            {...personColorProps(instructor.name)}
            name={instructor.name}
            supportingText={instructorSessionsLabel(instructor.id, sessions)}
          />
        ))}
      </div>
    </Card>
  );
}

InstructorsCard.propTypes = { sessions: PropTypes.array.isRequired };

export default function OverviewTab({ url, currencyCode, timeZone, publicUrl, publiclyViewable, registrationsPath, onViewRegistrations }) {
  const { loading, data, error } = useTabData(url);
  if (loading) return <ClassesLoading />;
  if (error) return <Alert color="error" role="alert" description="Could not load overview." />;
  const stats = data.stats || {};
  const classInstance = data.classInstance || {};

  return (
    <div>
      <Metrics stats={stats} classInstance={classInstance} currencyCode={currencyCode} timeZone={timeZone} />
      <DetailColumns
        rail={(
          <>
            <ClassDetailsCard classInstance={classInstance} currencyCode={currencyCode} timeZone={timeZone} />
            <InstructorsCard sessions={classInstance.class_sessions || []} />
          </>
        )}
      >
        {publicUrl && <PublicPageCard publicUrl={publicUrl} publiclyViewable={publiclyViewable} status={classInstance.status} visibility={classInstance.visibility} registrationClosed={classInstance.public_state === "registration_closed"} />}
        <RecentRegistrationsCard
          registrations={data.recentRegistrations || []}
          registrationsPath={registrationsPath}
          timeZone={timeZone}
          onViewRegistrations={onViewRegistrations}
        />
      </DetailColumns>
    </div>
  );
}

OverviewTab.propTypes = {
  url: PropTypes.string.isRequired,
  currencyCode: PropTypes.string,
  timeZone: PropTypes.string,
  publicUrl: PropTypes.string,
  publiclyViewable: PropTypes.bool,
  registrationsPath: PropTypes.string.isRequired,
  onViewRegistrations: PropTypes.func.isRequired,
};
